import type { JSONContent } from "@tiptap/core";
import type { Database } from "@/lib/database.types";
import type { SubscriptionTier } from "@/types/projects";

export type ProjectDocumentKind = "governance" | "story-spine";
export type LoreEntryType = Database["public"]["Enums"]["lore_type"];
export type LoreCanonStatus = Database["public"]["Enums"]["canon_status"];
export type GovernanceDocRecord = Database["public"]["Tables"]["governance_docs"]["Row"];
export type StorySpineRecord = Database["public"]["Tables"]["story_spines"]["Row"];
export type LoreEntryRecord = Database["public"]["Tables"]["lore_entries"]["Row"];
export type LoreEntryInsert = Database["public"]["Tables"]["lore_entries"]["Insert"];
export type LoreEntryUpdate = Database["public"]["Tables"]["lore_entries"]["Update"];

export interface ProjectDocumentView {
  id: string;
  kind: ProjectDocumentKind;
  body: JSONContent;
  updatedAt: string;
  titleKey: string;
  badgeKeys: string[];
  descriptionKey: string;
}

export interface LoreEntryView {
  id: string;
  projectId: string;
  type: LoreEntryType;
  title: string;
  body: JSONContent;
  mainBody: JSONContent;
  localRules: JSONContent;
  canonStatus: LoreCanonStatus;
  coverImagePath: string | null;
  coverImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  typeLabelKey: string;
  canonStatusLabelKey: string;
  scopeBadgeKey: string;
  coverGenerateActionLabel?: string | null;
  coverGenerationEligible?: boolean;
  hasCoverImage: boolean;
  hasLocalRulesSection: boolean;
}

export interface LoreEntryFilters {
  searchText: string;
  statusFilter: LoreCanonStatus | null;
  typeFilter: LoreEntryType | null;
}

export interface ProjectLoreCapacitySummary {
  tier: SubscriptionTier;
  loreEntriesUsed: number;
  loreEntriesLimit: number | null;
  loreEntriesRemaining: number | null;
  isUnlimited: boolean;
}

export interface LoreLimitPrompt {
  reason: "lore_entry_limit_reached";
  tier: SubscriptionTier;
  currentCount: number;
  allowedCount: number;
  recommendedTier: SubscriptionTier;
}

export interface CreateLoreEntryInput {
  title: string;
  type: LoreEntryType;
  body: JSONContent;
  localRules: JSONContent;
  coverImageFile?: File | null;
}

export interface UpdateLoreEntryInput {
  title: string;
  type: LoreEntryType;
  body: JSONContent;
  localRules: JSONContent;
  coverImageFile?: File | null;
  removeCoverImage?: boolean;
}

export interface UpdateProjectDocumentInput {
  body: JSONContent;
}

export type ProjectDocumentCollection = Record<ProjectDocumentKind, ProjectDocumentView | null>;

export const loreEntryTypes: LoreEntryType[] = [
  "magic_system",
  "technology",
  "political_system",
  "religion",
  "culture",
  "race_species",
  "faction",
  "location",
  "style_guide",
];

export function isLoreEntryTypeImageGenerationEligible(type: LoreEntryType) {
  return loreEntryTypes.includes(type);
}

export const loreCanonStatuses: LoreCanonStatus[] = [
  "draft",
  "proposed",
  "canon",
  "retconned",
];

export const loreEntryTypeLabels: Record<LoreEntryType, string> = {
  magic_system: "lore.types.magic_system",
  technology: "lore.types.technology",
  political_system: "lore.types.political_system",
  religion: "lore.types.religion",
  culture: "lore.types.culture",
  race_species: "lore.types.race_species",
  faction: "lore.types.faction",
  location: "lore.types.location",
  style_guide: "lore.types.style_guide",
};

export const loreCanonStatusLabels: Record<LoreCanonStatus, string> = {
  draft: "lore.statuses.draft",
  proposed: "lore.statuses.proposed",
  canon: "lore.statuses.canon",
  retconned: "lore.statuses.retconned",
};

export const projectDocumentTitleLabels: Record<ProjectDocumentKind, string> = {
  governance: "lore.documents.governance.title",
  "story-spine": "lore.documents.storySpine.title",
};

export const projectDocumentBadgeLabels: Record<ProjectDocumentKind, string[]> = {
  governance: ["lore.documents.required"],
  "story-spine": ["lore.documents.required", "lore.documents.livingDraft"],
};

export const projectDocumentDescriptionLabels: Record<ProjectDocumentKind, string> = {
  governance: "lore.documents.governance.description",
  "story-spine": "lore.documents.storySpine.description",
};

export const emptyLoreFilters: LoreEntryFilters = {
  searchText: "",
  statusFilter: null,
  typeFilter: null,
};

export function isScopedLoreType(type: LoreEntryType) {
  return type === "faction" || type === "location";
}

export function deriveLoreScopeBadge(type: LoreEntryType) {
  if (type === "faction") {
    return "lore.scopes.faction";
  }

  if (type === "location") {
    return "lore.scopes.location";
  }

  return "lore.scopes.world";
}

export function canTransitionLoreCanonStatus(
  currentStatus: LoreCanonStatus,
  nextStatus: LoreCanonStatus,
) {
  return currentStatus !== nextStatus && loreCanonStatuses.includes(nextStatus);
}
